import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiJson } from './api';

/**
 * Admin "debug mode" board for managing papers within a single
 * (level, subject, board, category) bucket.
 *
 * Each paper is a row with role slots (Question / Markscheme / Model / etc.).
 * Files from the on-disk pool can be dragged into and out of the slots
 * to assign or unassign a role. Files can also be renamed/deleted.
 *
 * Data sources:
 *   - GET /api/papers?level&subject&board&category
 *   - GET /api/admin/papers/scan?level&subject&board&category
 *
 * Mutations:
 *   - POST   /api/admin/papers                 create empty paper
 *   - PATCH  /api/admin/papers/:id             rename paper
 *   - DELETE /api/admin/papers/:id             delete paper (cascades files row)
 *   - POST   /api/admin/papers/:id/files       link file to role (existingPath)
 *   - DELETE /api/admin/papers/:id/files/:role unlink role
 *   - POST   /api/admin/files/rename-by-path   rename underlying file on disk
 *   - POST   /api/admin/files/delete-by-path   delete underlying file on disk
 */

const ROLES = [
  { key: 'question', label: 'Question Paper', short: 'QP' },
  { key: 'markscheme', label: 'Markscheme', short: 'MS' },
  { key: 'model', label: 'Model Answer', short: 'Model' },
  { key: 'examiner_report', label: 'Examiner Report', short: 'Examiner' },
  { key: 'solution_video', label: 'Solution Video', short: 'Video' },
  { key: 'formula_sheet', label: 'Formula Sheet', short: 'Formula' },
];

const DRAG_TYPE = 'application/x-srh-file';

function basename(p) {
  if (!p) return '';
  const s = String(p);
  const i = s.lastIndexOf('/');
  return i >= 0 ? s.slice(i + 1) : s;
}

export default function DebugPapersView({ level, subject, board, category, token, onChanged }) {
  const [papers, setPapers] = useState([]);
  const [scanFiles, setScanFiles] = useState([]); // [{name, relPath, subfolder}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [busyKey, setBusyKey] = useState(null); // string indicating which row/slot/file is mid-mutation
  const [statusMsg, setStatusMsg] = useState('');
  const [filter, setFilter] = useState('');
  const [showAssigned, setShowAssigned] = useState(false);

  const reload = useCallback(async () => {
    if (!level || !subject || !board || !category) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ level, subject, board, category });
      const [papersRes, scanRes] = await Promise.all([
        apiJson(`/api/papers?${params.toString()}`),
        apiJson(`/api/admin/papers/scan?${params.toString()}`, { token }),
      ]);
      setPapers(Array.isArray(papersRes.papers) ? papersRes.papers : []);
      setScanFiles(Array.isArray(scanRes.files) ? scanRes.files : []);
    } catch (err) {
      setError(err.message || 'Failed to load debug data');
    } finally {
      setLoading(false);
    }
  }, [level, subject, board, category, token]);

  useEffect(() => { reload(); }, [reload]);

  // Map: relPath -> { paperId, role } for fast "is this file already linked?" lookup
  const linkedByPath = useMemo(() => {
    const map = new Map();
    for (const p of papers) {
      for (const role of Object.keys(p.files || {})) {
        const f = p.files[role];
        if (f?.filePath) map.set(f.filePath, { paperId: p.id, paperTitle: p.title, role });
      }
    }
    return map;
  }, [papers]);

  const filteredScan = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return scanFiles.filter((f) => {
      const isAssigned = linkedByPath.has(f.relPath);
      if (!showAssigned && isAssigned) return false;
      if (!q) return true;
      return f.name.toLowerCase().includes(q) || (f.subfolder || '').toLowerCase().includes(q);
    });
  }, [scanFiles, filter, showAssigned, linkedByPath]);

  // ── Mutations ────────────────────────────────────────────────────────────

  const createPaper = useCallback(async () => {
    const title = window.prompt('Title for the new paper row', 'Untitled paper');
    if (!title) return;
    setBusyKey('new-paper');
    try {
      await apiJson('/api/admin/papers', {
        method: 'POST',
        token,
        body: { level, subject, board, category, title },
      });
      setStatusMsg('Paper created');
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Failed to create paper');
    } finally {
      setBusyKey(null);
    }
  }, [level, subject, board, category, token, reload, onChanged]);

  const renamePaper = useCallback(async (paper) => {
    const next = window.prompt('Rename paper', paper.title || '');
    if (!next || next === paper.title) return;
    setBusyKey(`paper-${paper.id}`);
    try {
      await apiJson(`/api/admin/papers/${paper.id}`, { method: 'PATCH', token, body: { title: next } });
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Rename failed');
    } finally {
      setBusyKey(null);
    }
  }, [token, reload, onChanged]);

  const deletePaper = useCallback(async (paper) => {
    if (!window.confirm(`Delete paper row "${paper.title}"?\n\nThis only removes the row + its file links. The underlying files stay on disk.`)) return;
    setBusyKey(`paper-${paper.id}`);
    try {
      await apiJson(`/api/admin/papers/${paper.id}`, { method: 'DELETE', token });
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusyKey(null);
    }
  }, [token, reload, onChanged]);

  const linkFileToRole = useCallback(async (paperId, role, relPath) => {
    setBusyKey(`slot-${paperId}-${role}`);
    try {
      // If this file is already linked to a different paper/role, unlink that first.
      const existing = linkedByPath.get(relPath);
      if (existing && (existing.paperId !== paperId || existing.role !== role)) {
        try {
          await apiJson(`/api/admin/papers/${existing.paperId}/files/${existing.role}`, { method: 'DELETE', token });
        } catch (_) { /* non-fatal */ }
      }
      await apiJson(`/api/admin/papers/${paperId}/files`, {
        method: 'POST',
        token,
        body: { role, existingPath: relPath },
      });
      setStatusMsg(`Linked ${basename(relPath)} → ${role}`);
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Link failed');
    } finally {
      setBusyKey(null);
    }
  }, [linkedByPath, token, reload, onChanged]);

  const unlinkRole = useCallback(async (paperId, role) => {
    setBusyKey(`slot-${paperId}-${role}`);
    try {
      await apiJson(`/api/admin/papers/${paperId}/files/${role}`, { method: 'DELETE', token });
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Unlink failed');
    } finally {
      setBusyKey(null);
    }
  }, [token, reload, onChanged]);

  const renameFile = useCallback(async (relPath) => {
    const oldName = basename(relPath);
    const next = window.prompt('Rename file (include extension)', oldName);
    if (!next || next === oldName) return;
    setBusyKey(`file-${relPath}`);
    try {
      await apiJson('/api/admin/files/rename-by-path', {
        method: 'POST',
        token,
        body: { relPath, newName: next },
      });
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Rename failed');
    } finally {
      setBusyKey(null);
    }
  }, [token, reload, onChanged]);

  const deleteFile = useCallback(async (relPath) => {
    if (!window.confirm(`Permanently delete ${basename(relPath)}?\n\nThis removes the file from disk and unlinks it from any paper.`)) return;
    setBusyKey(`file-${relPath}`);
    try {
      await apiJson('/api/admin/files/delete-by-path', { method: 'POST', token, body: { relPath } });
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusyKey(null);
    }
  }, [token, reload, onChanged]);

  // ── Drag helpers ────────────────────────────────────────────────────────

  const startDrag = (ev, payload) => {
    try {
      ev.dataTransfer.setData(DRAG_TYPE, JSON.stringify(payload));
      ev.dataTransfer.setData('text/plain', payload.relPath || '');
      ev.dataTransfer.effectAllowed = 'move';
    } catch (_) { /* ignore */ }
  };

  const readDrop = (ev) => {
    try {
      const raw = ev.dataTransfer.getData(DRAG_TYPE);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) { return null; }
  };

  const onSlotDrop = (paperId, role) => async (ev) => {
    ev.preventDefault();
    const payload = readDrop(ev);
    if (!payload?.relPath) return;
    // Source could be the unassigned pool (no source paper) or another slot.
    if (payload.source === 'slot' && payload.paperId === paperId && payload.role === role) return; // no-op
    await linkFileToRole(paperId, role, payload.relPath);
  };

  const onPoolDrop = async (ev) => {
    ev.preventDefault();
    const payload = readDrop(ev);
    if (!payload || payload.source !== 'slot') return;
    await unlinkRole(payload.paperId, payload.role);
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (!level || !subject || !board) return null;
  if (!category || category === 'All') {
    return (
      <div className="debug-board-empty">
        Select a single category above to enter debug mode for it.
      </div>
    );
  }

  return (
    <div className="debug-board" onDragOver={(e) => e.preventDefault()}>
      <div className="debug-board-header">
        <div>
          <strong>Debug board</strong>
          <span className="debug-crumbs">
            {level} · {subject} · {board} · {category}
          </span>
        </div>
        <div className="debug-board-actions">
          <button className="admin-btn primary" onClick={createPaper} disabled={busyKey === 'new-paper'}>
            + New paper row
          </button>
          <button className="admin-btn ghost" onClick={reload} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="admin-status error" role="alert">
          {error} <button className="admin-link" onClick={() => setError('')}>dismiss</button>
        </div>
      )}
      {statusMsg && !error && (
        <div className="admin-status success" aria-live="polite">{statusMsg}</div>
      )}

      <div className="debug-board-body">
        {/* ── Papers (rows of role slots) ─────────────────────────────── */}
        <div className="debug-papers">
          <div className="debug-section-title">
            Papers <span className="muted">({papers.length})</span>
          </div>

          {!papers.length && !loading && (
            <div className="debug-empty">No papers yet in this category. Click <em>+ New paper row</em>.</div>
          )}

          {papers.map((p) => (
            <div key={p.id} className={`debug-paper-row ${busyKey === `paper-${p.id}` ? 'busy' : ''}`}>
              <div className="debug-paper-meta">
                <div className="debug-paper-title" title={p.title}>{p.title}</div>
                <div className="debug-paper-row-actions">
                  <button className="admin-link" onClick={() => renamePaper(p)}>Rename</button>
                  <button className="admin-link danger" onClick={() => deletePaper(p)}>Delete row</button>
                </div>
              </div>
              <div className="debug-slot-grid">
                {ROLES.map((r) => {
                  const f = (p.files || {})[r.key];
                  const slotKey = `slot-${p.id}-${r.key}`;
                  return (
                    <div
                      key={r.key}
                      className={`debug-slot ${f ? 'filled' : 'empty'} ${busyKey === slotKey ? 'busy' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={onSlotDrop(p.id, r.key)}
                    >
                      <div className="debug-slot-label">{r.label}</div>
                      {f ? (
                        <div
                          className="debug-slot-file"
                          draggable
                          onDragStart={(e) => startDrag(e, { source: 'slot', paperId: p.id, role: r.key, relPath: f.filePath })}
                          title={f.filePath}
                        >
                          <span className="debug-slot-filename">{basename(f.filePath)}</span>
                          <button
                            className="debug-slot-x"
                            onClick={() => unlinkRole(p.id, r.key)}
                            aria-label={`Unlink ${r.label}`}
                            title="Unlink (file stays on disk)"
                          >×</button>
                        </div>
                      ) : (
                        <div className="debug-slot-placeholder">drop file here</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* ── File pool ──────────────────────────────────────────────── */}
        <div
          className="debug-pool"
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDrop={onPoolDrop}
        >
          <div className="debug-section-title">
            Files <span className="muted">({filteredScan.length}/{scanFiles.length})</span>
          </div>

          <div className="debug-pool-controls">
            <input
              type="text"
              className="debug-filter"
              placeholder="Filter files…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <label className="debug-toggle">
              <input
                type="checkbox"
                checked={showAssigned}
                onChange={(e) => setShowAssigned(e.target.checked)}
              />
              Show assigned
            </label>
          </div>

          <div className="debug-pool-hint">
            Drag a file onto a slot to assign it. Drag a slot back here to unlink.
          </div>

          {!scanFiles.length && !loading && (
            <div className="debug-empty">No files on disk in this category.</div>
          )}

          <ul className="debug-pool-list">
            {filteredScan.map((f) => {
              const linked = linkedByPath.get(f.relPath);
              const fileKey = `file-${f.relPath}`;
              return (
                <li
                  key={f.relPath}
                  className={`debug-pool-item ${linked ? 'is-linked' : ''} ${busyKey === fileKey ? 'busy' : ''}`}
                  draggable
                  onDragStart={(e) => startDrag(e, { source: 'pool', relPath: f.relPath })}
                  title={f.relPath}
                >
                  <div className="debug-pool-name">
                    {f.name}
                    {f.subfolder && <span className="debug-pool-sub"> · {f.subfolder}</span>}
                  </div>
                  {linked && (
                    <div className="debug-pool-linked" title={`Linked to "${linked.paperTitle}" as ${linked.role}`}>
                      → {linked.role}
                    </div>
                  )}
                  <div className="debug-pool-actions">
                    <button className="admin-link" onClick={() => renameFile(f.relPath)}>Rename</button>
                    <button className="admin-link danger" onClick={() => deleteFile(f.relPath)}>Delete</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
